import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import {
	useGenerateSmartProgram,
	usePollGenerationStatus,
} from "../../../hooks/useTreatmentPlans";
import { GenerateSmartProgramResponse } from "../../../services/treatmentPlan.service";
import SmartProgramPreview from "./SmartProgramPreview";
import SmartProgramChat from "./SmartProgramChat";

interface SmartProgramConfigProps {
	treatmentPlanId: string;
	onApprove: (result: GenerateSmartProgramResponse) => void;
	onCancel: () => void;
}

type AuthorMode = "ONE_SHOT" | "CHAT";

const SmartProgramConfig: React.FC<SmartProgramConfigProps> = ({
	treatmentPlanId,
	onApprove,
	onCancel,
}) => {
	const [authorMode, setAuthorMode] = useState<AuthorMode>("ONE_SHOT");
	const [description, setDescription] = useState("");
	const [preferredMode, setPreferredMode] = useState<string | null>(null);
	const [generatedResult, setGeneratedResult] =
		useState<GenerateSmartProgramResponse | null>(null);
	const [isApproving, setIsApproving] = useState(false);
	const [isPolling, setIsPolling] = useState(false);

	const generateMutation = useGenerateSmartProgram();
	const pollQuery = usePollGenerationStatus(treatmentPlanId, isPolling);

	// Watch polling result
	useEffect(() => {
		if (!pollQuery.data) return;

		const { status, result } = pollQuery.data;

		if (status === "SUCCESS" && result) {
			setIsPolling(false);
			setGeneratedResult(result);
		} else if (status === "FAILED") {
			setIsPolling(false);
			generateMutation.reset();
		}
	}, [pollQuery.data]);

	const startGeneration = (
		nextDescription: string,
		nextPreferredMode?: string | null,
	) => {
		generateMutation.mutate(
			{
				id: treatmentPlanId,
				payload: {
					description: nextDescription,
					preferredMode: nextPreferredMode || undefined,
				},
			},
			{
				onSuccess: () => {
					setIsPolling(true);
				},
			},
		);
	};

	const handleGenerate = () => {
		if (!description.trim() || description.trim().length < 10) return;
		startGeneration(description.trim(), preferredMode);
	};

	const handleChatGenerate = (renderedDescription: string) => {
		startGeneration(renderedDescription, preferredMode);
	};

	const handleApprove = async () => {
		if (!generatedResult) return;
		setIsApproving(true);
		try {
			onApprove(generatedResult);
		} finally {
			setIsApproving(false);
		}
	};

	const handleRegenerate = () => {
		setGeneratedResult(null);
		if (authorMode === "ONE_SHOT") {
			handleGenerate();
		}
		// In CHAT mode, regenerate drops back to the chat view so the user can
		// keep editing and click "Generate workflow" again.
	};

	const isGenerating = generateMutation.isPending || isPolling;
	const pollStatus = pollQuery.data?.status;
	const pollError = pollQuery.data?.error;

	if (generatedResult) {
		return (
			<SmartProgramPreview
				previewHtml={generatedResult.previewHtml}
				onApprove={handleApprove}
				onRegenerate={handleRegenerate}
				onCancel={() => {
					setGeneratedResult(null);
				}}
				isApproving={isApproving}
			/>
		);
	}

	return (
		<Box>
			<Box sx={{ mb: 2 }}>
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{ display: "block", mb: 1 }}
				>
					Authoring mode
				</Typography>
				<ToggleButtonGroup
					value={authorMode}
					exclusive
					onChange={(_, value) => value && setAuthorMode(value as AuthorMode)}
					size="small"
					disabled={isGenerating}
				>
					<ToggleButton value="ONE_SHOT">One-shot</ToggleButton>
					<ToggleButton value="CHAT">Chat</ToggleButton>
				</ToggleButtonGroup>
			</Box>

			{authorMode === "ONE_SHOT" && (
				<>
					<Typography variant="subtitle1" sx={{ mb: 1 }}>
						Describe Your Program
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						Describe the treatment program workflow in plain English. The AI
						will generate a structured configuration based on your description
						and available events.
					</Typography>

					<TextField
						fullWidth
						multiline
						rows={4}
						value={description}
						onChange={(e) => setDescription(e.target.value)}
						placeholder="Example: A 90-day substance use treatment program with daily urine tests in the first 30 days, tapering to 3 times per week. Include weekly counseling sessions and daily medication reminders at the patient's preferred dosage time."
						disabled={isGenerating}
						sx={{ mb: 2 }}
					/>

					<Box sx={{ mb: 2 }}>
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ mb: 1, display: "block" }}
						>
							Preferred Mode (optional)
						</Typography>
						<ToggleButtonGroup
							value={preferredMode}
							exclusive
							onChange={(_, value) => setPreferredMode(value)}
							size="small"
							disabled={isGenerating}
						>
							<ToggleButton value="LINEAR">Linear (Fixed Days)</ToggleButton>
							<ToggleButton value="REPEATING">Repeating (Cycle)</ToggleButton>
						</ToggleButtonGroup>
					</Box>

					{generateMutation.isError && (
						<Alert severity="error" sx={{ mb: 2 }}>
							{(generateMutation.error as Error)?.message ||
								"Failed to start generation. Please try again."}
						</Alert>
					)}

					{pollStatus === "FAILED" && (
						<Alert severity="error" sx={{ mb: 2 }}>
							{pollError || "AI generation failed. Please try again."}
						</Alert>
					)}

					{isPolling && (
						<Alert severity="info" sx={{ mb: 2 }}>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
								<CircularProgress size={16} />
								<Typography variant="body2">
									{pollStatus === "IN_PROGRESS"
										? "AI is generating your program configuration..."
										: "Starting generation..."}
								</Typography>
							</Box>
						</Alert>
					)}

					<Box sx={{ display: "flex", gap: 2 }}>
						<Button
							variant="contained"
							startIcon={
								isGenerating ? (
									<CircularProgress size={20} color="inherit" />
								) : (
									<AutoAwesomeIcon />
								)
							}
							onClick={handleGenerate}
							disabled={isGenerating || description.trim().length < 10}
						>
							{isGenerating ? "Generating..." : "Generate with AI"}
						</Button>
						<Button
							variant="outlined"
							onClick={onCancel}
							disabled={isGenerating}
						>
							Cancel
						</Button>
					</Box>
				</>
			)}

			{authorMode === "CHAT" && (
				<>
					{generateMutation.isError && (
						<Alert severity="error" sx={{ mb: 2 }}>
							{(generateMutation.error as Error)?.message ||
								"Failed to start generation. Please try again."}
						</Alert>
					)}

					{/*
						Validation/FAILED errors are NOT shown here in chat mode —
						they're auto-injected into the chat as a user turn (see
						SmartProgramChat#finalGenerationError) so the assistant can
						help the clinician refine the design conversationally.
					*/}

					{isPolling && (
						<Alert severity="info" sx={{ mb: 2 }}>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
								<CircularProgress size={16} />
								<Typography variant="body2">
									{pollStatus === "IN_PROGRESS"
										? "AI is generating your program configuration..."
										: "Starting generation..."}
								</Typography>
							</Box>
						</Alert>
					)}

					<SmartProgramChat
						treatmentPlanId={treatmentPlanId}
						finalGenerationError={
							pollStatus === "FAILED" ? pollError ?? null : null
						}
						finalGenerationJobId={pollQuery.data?.jobId ?? null}
						onGenerate={handleChatGenerate}
						onCancel={onCancel}
						isFinalGenerating={isGenerating}
					/>
				</>
			)}
		</Box>
	);
};

export default SmartProgramConfig;
